import { httpClient } from '@/shared/services/api/http-client';
import type {
  AccountModeration,
  ActionResult,
  ActivitySector,
  ActivitySectorSuggestion,
  AgentApplication,
  AgentApplicationDecision,
  Article,
  ArticleCreate,
  ArticleUpdate,
  BillingOverview,
  Sector,
  AuditLog,
  AuthTokens,
  Bookmark,
  Cart,
  Category,
  CategoryCreate,
  CategoryUpdate,
  CounterSale,
  Elevation,
  HiddenNotifications,
  InvoiceDocumentLink,
  PinStatus,
  ShopAudienceReport,
  ShopBillingSummary,
  ShopRevenueReport,
  CertificationRequestResult,
  Checkout,
  Comment,
  Community,
  CommunityComment,
  CommunityDetail,
  CommunityJoinPolicy,
  CommunityMember,
  CommunityMemberRole,
  CommunityPost,
  CommunityPrivacy,
  Call,
  CallKind,
  Contact,
  Conversation,
  ConversationDetail,
  Dispute,
  DisputeDecision,
  FeedPage,
  FeedSignal,
  GoogleAuthResult,
  GoogleLinkStatus,
  Inspection,
  Invoice,
  Kyc,
  Listing,
  Live,
  LiveCreate,
  LiveDetail,
  LiveJoin,
  LiveMessage,
  LiveProductInput,
  ListingDetail,
  ListingMediaInput,
  Me,
  MobileMoneyOperator,
  NextProductCode,
  Notification,
  Offer,
  Order,
  OrderDetail,
  Page,
  PaymentAccount,
  PaymentDeclaration,
  PaymentReferenceCheck,
  PermissionRule,
  PlanCatalog,
  PlatformSettings,
  PlatformStats,
  Product,
  ProductStock,
  ProfileUpdate,
  PublicProfile,
  PushDevice,
  ReactionKind,
  ReactionSummary,
  Receipt,
  ReceiptVerification,
  Report,
  Sanction,
  SanctionReason,
  SanctionType,
  ChatMessage,
  VoiceNoteInput,
  Shop,
  ShopCreate,
  ShopDashboard,
  StockMovement,
  StockMovementInput,
  StockSummary,
  ShopDetail,
  ShopMember,
  ShopPayment,
  ShopSubscription,
  ShopUpdate,
  UrgentRequest,
  UploadedFile,
  UserListItem,
  UserProfile,
  OtpSent,
  VideoTicket,
} from '@/shared/services/api/types';

/**
 * Toutes les requetes de l'application, regroupées par domaine.
 *
 * Une seule couche connait les chemins d'URL : les écrans appellent
 * `api.listings.feed({ … })`. Les types viennent du contrat généré, donc un
 * champ renommé coté backend casse la compilation ici, pas en production.
 */
type Query = Record<string, string | number | boolean | undefined | null>;

export const api = {
  auth: {
    register: (body: { firstName: string; lastName: string; phone: string; email?: string }) =>
      httpClient.post<OtpSent>('/auth/register', body, { anonymous: true }),

    requestOtp: (body: { phone: string; purpose: 'REGISTRATION' | 'LOGIN' }) =>
      httpClient.post<OtpSent>('/auth/otp/request', body, { anonymous: true }),

    verifyOtp: (body: { phone: string; code: string; purpose: 'REGISTRATION' | 'LOGIN' }) =>
      httpClient.post<AuthTokens>('/auth/otp/verify', body, { anonymous: true }),

    loginAgent: (body: { matricule: string; password: string }) =>
      httpClient.post<AuthTokens>('/auth/login/agent', body, { anonymous: true }),

    /**
     * Connexion Google. L'ouverture de la fenetre ne passe pas par ici : c'est
     * une adresse a ouvrir dans le navigateur du système, construite dans
     * `features/auth/lib/google-flow`. Ne restent que les échanges JSON.
     */
    googleSession: (body: { ticket: string }) =>
      httpClient.post<GoogleAuthResult>('/auth/google/session', body, { anonymous: true }),

    googleRegister: (body: {
      registrationToken: string;
      firstName: string;
      lastName: string;
      phone: string;
    }) => httpClient.post<OtpSent>('/auth/google/register', body, { anonymous: true }),

    googleLinkRequest: (body: { linkToken: string }) =>
      httpClient.post<OtpSent>('/auth/google/link/request', body, { anonymous: true }),

    googleLinkVerify: (body: { linkToken: string; code: string }) =>
      httpClient.post<AuthTokens>('/auth/google/link/verify', body, { anonymous: true }),

    /** Depuis les réglages, compte connecté : relier, détacher, connaitre l'état. */
    googleLinkStatus: () => httpClient.get<GoogleLinkStatus>('/auth/google/link'),
    googleLink: (body: { ticket: string }) =>
      httpClient.post<GoogleLinkStatus>('/auth/google/link', body),
    googleUnlink: () => httpClient.delete<GoogleLinkStatus>('/auth/google/link'),

    me: () => httpClient.get<Me>('/auth/me'),
    permissions: () => httpClient.get<{ rules: Array<PermissionRule> }>('/auth/me/permissions'),
    logout: () => httpClient.post<{ message: string }>('/auth/logout'),
  },

  /**
   * Le code de confidentialité : ce que l'application demande avant tout geste
   * qui touche à l'argent. Il se vérifie sur le serveur ; le téléphone ne garde
   * que la preuve, courte, qu'il vient d'être saisi.
   */
  pin: {
    status: () => httpClient.get<PinStatus>('/auth/pin'),
    /** Créer, ou remplacer : le remplacement exige le code actuel. */
    set: (pin: string, currentPin?: string) =>
      httpClient.put<Elevation>('/auth/pin', { pin, currentPin }),
    verify: (pin: string) => httpClient.post<Elevation>('/auth/pin/verify', { pin }),
    /** Code oublié : un SMS part sur le numéro du compte. */
    requestReset: () => httpClient.post<OtpSent>('/auth/pin/reset/request'),
    reset: (code: string, pin: string) =>
      httpClient.post<Elevation>('/auth/pin/reset', { code, pin }),
  },

  users: {
    /** Annuaire réservé à l'administration : choisir un agent, un compte à sanctionner. */
    directory: (query: Query) => httpClient.get<Page<UserListItem>>('/users', { query }),
    profile: () => httpClient.get<UserProfile>('/users/me'),
    updateProfile: (body: ProfileUpdate) => httpClient.patch<UserProfile>('/users/me', body),
    publicProfile: (id: string) => httpClient.get<PublicProfile>(`/users/${id}`),
    /** Suppression logique du compte : révoque toutes les sessions. */
    deleteMe: () => httpClient.delete<{ message: string }>('/users/me'),
  },

  /**
   * Modération d'un compte (B.4 étape 2), administration seule.
   *
   * Trois décisions, et la fiche qui permet de les prendre. La trace reste une
   * mesure ordinaire, dans le meme registre que celles prononcées en arbitrage :
   * `sanctions.list({ userId })` la retrouve.
   */
  accountModeration: {
    detail: (id: string) => httpClient.get<AccountModeration>(`/users/${id}/moderation`),

    /** De 1 jour à 1 an ; `durationDays` absent = sans limite de durée. */
    suspend: (
      id: string,
      body: {
        reasonCode: SanctionReason;
        detail: string;
        durationDays?: number;
        includeShops?: boolean;
      },
    ) => httpClient.post<AccountModeration>(`/users/${id}/suspend`, body),

    /** À tout moment : toutes les mesures en vigueur tombent d'un coup. */
    reactivate: (id: string, note?: string) =>
      httpClient.post<AccountModeration>(`/users/${id}/reactivate`, { note }),

    /** Suppression logique, motif consigné au journal d'audit. */
    remove: (id: string, reason: string) =>
      httpClient.delete<ActionResult>(`/users/${id}`, { body: { reason } }),
  },

  kyc: {
    submit: (body: {
      documentType: 'CIN' | 'PASSPORT';
      documentNumber: string;
      frontUrl: string;
      backUrl?: string;
      selfieUrl?: string;
    }) => httpClient.post<Kyc>('/kyc', body),
    mine: () => httpClient.get<Array<Kyc>>('/kyc/me'),
    queue: (query: Query) => httpClient.get<Page<Kyc>>('/kyc', { query }),
    review: (id: string, approve: boolean, rejectionReason?: string) =>
      httpClient.patch<Kyc>(`/kyc/${id}/review`, { approve, rejectionReason }),
  },

  paymentAccounts: {
    list: (shopId?: string) =>
      httpClient.get<Array<PaymentAccount>>('/payment-accounts', { query: { shopId } }),
    create: (body: {
      provider: 'MVOLA' | 'ORANGE_MONEY' | 'AIRTEL_MONEY' | 'BANK';
      accountNumber: string;
      accountName: string;
      shopId?: string;
      isDefault?: boolean;
    }) => httpClient.post<PaymentAccount>('/payment-accounts', body),
    update: (
      id: string,
      body: { accountNumber?: string; accountName?: string; isDefault?: boolean },
    ) => httpClient.patch<PaymentAccount>(`/payment-accounts/${id}`, body),
    remove: (id: string) => httpClient.delete<{ message: string }>(`/payment-accounts/${id}`),
  },

  shops: {
    list: (query: Query) => httpClient.get<Page<Shop>>('/shops', { query }),
    followed: (query: Query) => httpClient.get<Page<Shop>>('/shops/followed', { query }),
    detail: (id: string) => httpClient.get<ShopDetail>(`/shops/${id}`),
    create: (body: ShopCreate) => httpClient.post<ShopDetail>('/shops', body),
    update: (id: string, body: ShopUpdate) => httpClient.patch<ShopDetail>(`/shops/${id}`, body),
    follow: (id: string) => httpClient.post<{ message: string }>(`/shops/${id}/follow`),
    unfollow: (id: string) => httpClient.delete<{ message: string }>(`/shops/${id}/follow`),
    members: (id: string, status?: string) =>
      httpClient.get<Array<ShopMember>>(`/shops/${id}/members`, { query: { status } }),
    applyAsMember: (id: string) => httpClient.post<ShopMember>(`/shops/${id}/members/apply`),
    decideMember: (id: string, memberId: string, approve: boolean, capabilities?: Array<string>) =>
      httpClient.patch<ShopMember>(`/shops/${id}/members/${memberId}/decide`, {
        approve,
        capabilities,
      }),
    /** Décision 6 : on invite un compte existant, on n'en crée jamais. */
    inviteMember: (id: string, phone: string, capabilities?: Array<string>) =>
      httpClient.post<ShopMember>(`/shops/${id}/members/invite`, { phone, capabilities }),
    /** L'invité répond : rejoindre l'équipe ou décliner. */
    respondInvite: (id: string, memberId: string, accept: boolean) =>
      httpClient.post<ShopMember>(`/shops/${id}/members/${memberId}/respond`, { accept }),
    /** Le responsable recoche les accès d'un membre, à tout moment. */
    configureMember: (id: string, memberId: string, capabilities: Array<string>) =>
      httpClient.patch<ShopMember>(`/shops/${id}/members/${memberId}/capabilities`, {
        capabilities,
      }),
    revokeMember: (id: string, memberId: string) =>
      httpClient.delete<{ message: string }>(`/shops/${id}/members/${memberId}`),
    /** Les chiffres de la boutique, agrégés par le serveur. Responsable seul. */
    dashboard: (id: string) => httpClient.get<ShopDashboard>(`/shops/${id}/dashboard`),
    /**
     * Les mouvements d'argent : versements déclarés, confirmés, refusés. Ils se
     * filtrent par date (`from`, `to`), état, moyen, et par un numéro de
     * commande, de transaction ou de reçu, ou un nom (`search`).
     */
    payments: (id: string, query: Query) =>
      httpClient.get<Page<ShopPayment>>(`/shops/${id}/payments`, { query }),
    /** Les encaissements d'une période : `from` et `to` en AAAA-MM-JJ, facultatifs. */
    revenue: (id: string, query: Query) =>
      httpClient.get<ShopRevenueReport>(`/shops/${id}/revenue`, { query }),
    /** L'audience d'une période, avec le type des visiteurs. */
    audience: (id: string, query: Query) =>
      httpClient.get<ShopAudienceReport>(`/shops/${id}/audience`, { query }),
  },

  /** Gestion des stocks (brique 5) : compteurs, journal et chiffres, calculés par le serveur. */
  stock: {
    summary: (shopId: string) => httpClient.get<StockSummary>(`/shops/${shopId}/stock/summary`),
    overview: (shopId: string, query: Query) =>
      httpClient.get<Page<ProductStock>>(`/shops/${shopId}/stock`, { query }),
    product: (productId: string) => httpClient.get<ProductStock>(`/products/${productId}/stock`),
    movements: (productId: string, query: Query) =>
      httpClient.get<Page<StockMovement>>(`/products/${productId}/stock/movements`, { query }),
    /** Un mouvement saisi par le vendeur : entrée, sortie, blocage, inventaire. */
    move: (productId: string, body: StockMovementInput) =>
      httpClient.post<StockMovement>(`/products/${productId}/stock/movements`, body),
  },

  agentApplications: {
    list: (query: Query) =>
      httpClient.get<Page<AgentApplication>>('/agent-applications', { query }),
    create: (body: { type: 'CERTIFIER' | 'SHOP_ASSISTANT'; zone?: string; message?: string }) =>
      httpClient.post<AgentApplication>('/agent-applications', body),
    decide: (id: string, approve: boolean, rejectionReason?: string) =>
      httpClient.patch<AgentApplicationDecision>(`/agent-applications/${id}/decide`, {
        approve,
        rejectionReason,
      }),
  },

  /** Les rayons d'une boutique : son classement interne, tenu par le vendeur. */
  sectors: {
    list: (shopId: string) => httpClient.get<Array<Sector>>('/sectors', { query: { shopId } }),
    create: (shopId: string, label: string) =>
      httpClient.post<Sector>('/sectors', { shopId, label }),
    rename: (id: string, label: string) => httpClient.patch<Sector>(`/sectors/${id}`, { label }),
    remove: (id: string) => httpClient.delete<{ message: string }>(`/sectors/${id}`),
  },

  /**
   * Le référentiel des secteurs d'activité, tenu par l'administration. La
   * liste sert au select de l'ouverture de boutique ; le reste est le CRUD de
   * l'espace admin, et `suggestions` les libellés « Autre » à promouvoir.
   */
  activitySectors: {
    list: () => httpClient.get<Array<ActivitySector>>('/activity-sectors'),
    suggestions: () =>
      httpClient.get<Array<ActivitySectorSuggestion>>('/activity-sectors/suggestions'),
    create: (label: string) => httpClient.post<ActivitySector>('/activity-sectors', { label }),
    rename: (id: string, label: string) =>
      httpClient.patch<ActivitySector>(`/activity-sectors/${id}`, { label }),
    remove: (id: string) => httpClient.delete<{ message: string }>(`/activity-sectors/${id}`),
  },

  catalog: {
    categories: () => httpClient.get<Array<Category>>('/categories'),
    /** Le référentiel des articles, entier ou restreint à une catégorie. */
    articles: (categoryId?: string) =>
      httpClient.get<Array<Article>>('/articles', { query: { categoryId } }),
    /**
     * Le référentiel côté administration : catégories et articles se créent,
     * se modifient et se retirent tant que rien ne s'y range.
     */
    createCategory: (body: CategoryCreate) => httpClient.post<Category>('/categories', body),
    updateCategory: (id: string, body: CategoryUpdate) =>
      httpClient.patch<Category>(`/categories/${id}`, body),
    removeCategory: (id: string) => httpClient.delete<{ message: string }>(`/categories/${id}`),
    createArticle: (body: ArticleCreate) => httpClient.post<Article>('/articles', body),
    updateArticle: (id: string, body: ArticleUpdate) =>
      httpClient.patch<Article>(`/articles/${id}`, body),
    removeArticle: (id: string) => httpClient.delete<{ message: string }>(`/articles/${id}`),
    products: (query: Query) => httpClient.get<Page<Product>>('/products', { query }),
    product: (id: string) => httpClient.get<Product>(`/products/${id}`),
    createProduct: (body: Record<string, unknown>) => httpClient.post<Product>('/products', body),
    /**
     * Le code que recevra le produit : préfixe de la catégorie et numéro propre
     * au responsable. Sert à l'afficher avant la validation.
     */
    nextProductCode: (shopId: string, categoryId: string, articleId?: string) =>
      httpClient.get<NextProductCode>('/products/next-code', {
        query: { shopId, categoryId, articleId },
      }),
    updateProduct: (id: string, body: Record<string, unknown>) =>
      httpClient.patch<Product>(`/products/${id}`, body),
    removeProduct: (id: string) => httpClient.delete<{ message: string }>(`/products/${id}`),
  },

  listings: {
    feed: (query: Query) => httpClient.get<Page<Listing>>('/listings', { query }),
    detail: (id: string) => httpClient.get<ListingDetail>(`/listings/${id}`),
    create: (body: {
      shopId: string;
      /** Absent = publication directe : titre et média exigés, ni prix ni promotion. */
      productCode?: string;
      title?: string;
      body?: string;
      /** Photos et vidéos, dans l'ordre d'affichage. Dix au plus, toutes natures confondues. */
      media?: Array<ListingMediaInput>;
      isPromotion?: boolean;
      promoPrice?: number;
      /** Période de la promotion, en ISO. Les deux bornes sont facultatives. */
      promoStartsAt?: string | null;
      promoEndsAt?: string | null;
    }) => httpClient.post<ListingDetail>('/listings', body),
    close: (id: string) => httpClient.post<ListingDetail>(`/listings/${id}/close`),
    /** Remet en ligne une annonce que le vendeur avait fermée. */
    reopen: (id: string) => httpClient.post<ListingDetail>(`/listings/${id}/reopen`),
    update: (id: string, body: Record<string, unknown>) =>
      httpClient.patch<ListingDetail>(`/listings/${id}`, body),
    /** Retrait par l'administration : l'annonce sort du fil, avec un motif tracé. */
    moderate: (id: string, reason: string) =>
      httpClient.delete<{ message: string }>(`/listings/${id}`, { body: { reason } }),
    /** Pose ou remplace ma réaction ; la réponse porte les compteurs après le geste. */
    react: (id: string, type: ReactionKind) =>
      httpClient.put<ReactionSummary>(`/listings/${id}/reaction`, { type }),
    /** Idempotent : retirer une réaction déjà absente rend le même état. */
    unreact: (id: string) => httpClient.delete<ReactionSummary>(`/listings/${id}/reaction`),
    bookmark: (id: string) => httpClient.post<{ message: string }>(`/listings/${id}/bookmark`),
    unbookmark: (id: string) => httpClient.delete<{ message: string }>(`/listings/${id}/bookmark`),
    comments: (id: string, query: Query) =>
      httpClient.get<Page<Comment>>(`/listings/${id}/comments`, { query }),
    /** Avec `parentId`, le message répond à un commentaire : le droit de réponse. */
    comment: (id: string, content: string, parentId?: string) =>
      httpClient.post<Comment>(`/listings/${id}/comments`, { content, parentId }),
    /** Réservé à l'auteur : le commentaire porte ensuite sa date de modification. */
    updateComment: (commentId: string, content: string) =>
      httpClient.patch<Comment>(`/comments/${commentId}`, { content }),
    removeComment: (commentId: string) =>
      httpClient.delete<{ message: string }>(`/comments/${commentId}`),
  },

  bookmarks: {
    list: (query: Query) => httpClient.get<Page<Bookmark>>('/bookmarks', { query }),
  },

  /** Le fil recommandé (brique 1) et ses signaux, remis par lots. */
  feed: {
    forYou: (query: Query) => httpClient.get<FeedPage>('/feed', { query }),
    events: (events: Array<FeedSignal>, options: { keepalive?: boolean } = {}) =>
      httpClient.post<{ accepted: number }>('/feed/events', { events }, options),
  },

  /** La vente en direct (brique 2). */
  lives: {
    list: (query: Query) => httpClient.get<Page<Live>>('/lives', { query }),
    detail: (id: string) => httpClient.get<LiveDetail>(`/lives/${id}`),
    create: (body: LiveCreate) => httpClient.post<LiveDetail>('/lives', body),
    start: (id: string) => httpClient.post<LiveDetail>(`/lives/${id}/start`),
    end: (id: string) => httpClient.post<LiveDetail>(`/lives/${id}/end`),
    addProduct: (id: string, body: LiveProductInput) =>
      httpClient.post<LiveDetail>(`/lives/${id}/products`, body),
    removeProduct: (id: string, liveProductId: string) =>
      httpClient.delete<LiveDetail>(`/lives/${id}/products/${liveProductId}`),
    /** `null` désépingle. */
    pin: (id: string, liveProductId: string | null) =>
      httpClient.put<LiveDetail>(`/lives/${id}/pin`, { liveProductId }),
    /** À rappeler à chaque reconnexion du canal : un socket neuf ne connaît aucune pièce. */
    join: (id: string) => httpClient.post<LiveJoin>(`/lives/${id}/join`),
    leave: (id: string) => httpClient.post<ActionResult>(`/lives/${id}/leave`),
    messages: (id: string, query: Query) =>
      httpClient.get<Page<LiveMessage>>(`/lives/${id}/messages`, { query }),
    send: (id: string, content: string) =>
      httpClient.post<LiveMessage>(`/lives/${id}/messages`, { content }),
    removeMessage: (id: string, messageId: string) =>
      httpClient.delete<ActionResult>(`/lives/${id}/messages/${messageId}`),
    mute: (id: string, userId: string, muted: boolean) =>
      httpClient.put<ActionResult>(`/lives/${id}/viewers/${userId}/mute`, { muted }),
  },

  reports: {
    create: (body: {
      targetType: 'LISTING' | 'COMMENT' | 'USER' | 'SHOP' | 'COMMUNITY_POST';
      targetId: string;
      reason: string;
      details?: string;
    }) => httpClient.post<Report>('/reports', body),
    list: (query: Query) => httpClient.get<Page<Report>>('/reports', { query }),
    handle: (id: string, action: 'ACTIONED' | 'DISMISSED', resolutionNote?: string) =>
      httpClient.patch<Report>(`/reports/${id}/handle`, { action, resolutionNote }),
  },

  communities: {
    /** scope : discover (ouvertes), mine (les miennes), requests (administration). */
    list: (query: Query) => httpClient.get<Page<Community>>('/communities', { query }),
    detail: (id: string) => httpClient.get<CommunityDetail>(`/communities/${id}`),
    /** La demande d'ouverture : rien ne s'ouvre, l'administration décide. */
    create: (body: {
      name: string;
      purpose: string;
      targetMembers: string;
      rules: string;
      coverUrl?: string;
      privacy?: CommunityPrivacy;
      joinPolicy?: CommunityJoinPolicy;
      requiresPostApproval?: boolean;
      /** L'engagement sur la charte : obligatoire, le serveur refuse sans lui. */
      charterAccepted: boolean;
    }) => httpClient.post<CommunityDetail>('/communities', body),
    update: (id: string, body: Record<string, unknown>) =>
      httpClient.patch<CommunityDetail>(`/communities/${id}`, body),
    cancelRequest: (id: string) => httpClient.delete<ActionResult>(`/communities/${id}`),
    /** Décision de l'administration sur la demande d'ouverture. */
    decide: (id: string, approve: boolean, rejectionReason?: string) =>
      httpClient.post<CommunityDetail>(`/communities/${id}/decide`, { approve, rejectionReason }),
    suspend: (id: string, reason: string) =>
      httpClient.post<CommunityDetail>(`/communities/${id}/suspend`, { reason }),
    reactivate: (id: string) => httpClient.post<CommunityDetail>(`/communities/${id}/reactivate`),
    join: (id: string) => httpClient.post<ActionResult>(`/communities/${id}/join`),
    leave: (id: string) => httpClient.post<ActionResult>(`/communities/${id}/leave`),
    members: (id: string, query: Query) =>
      httpClient.get<Page<CommunityMember>>(`/communities/${id}/members`, { query }),
    decideMember: (id: string, memberId: string, approve: boolean, reason?: string) =>
      httpClient.post<ActionResult>(`/communities/${id}/members/${memberId}/decide`, {
        approve,
        reason,
      }),
    removeMember: (id: string, memberId: string, reason?: string) =>
      httpClient.post<ActionResult>(`/communities/${id}/members/${memberId}/remove`, { reason }),
    /** Réservé au créateur : nommer ou retirer un co-administrateur. */
    setMemberRole: (id: string, memberId: string, role: CommunityMemberRole) =>
      httpClient.patch<ActionResult>(`/communities/${id}/members/${memberId}/role`, { role }),
    /** scope : feed (le fil), pending (la file de validation des administrateurs). */
    posts: (id: string, query: Query) =>
      httpClient.get<Page<CommunityPost>>(`/communities/${id}/posts`, { query }),
    createPost: (
      id: string,
      body: {
        content?: string;
        media?: Array<ListingMediaInput>;
        /** Annonce du fil partagée dans le groupe. */
        sharedListingId?: string;
      },
    ) => httpClient.post<CommunityPost>(`/communities/${id}/posts`, body),
    post: (postId: string) => httpClient.get<CommunityPost>(`/community-posts/${postId}`),
    updatePost: (postId: string, content: string) =>
      httpClient.patch<CommunityPost>(`/community-posts/${postId}`, { content }),
    deletePost: (postId: string) => httpClient.delete<ActionResult>(`/community-posts/${postId}`),
    decidePost: (postId: string, approve: boolean, reason?: string) =>
      httpClient.post<CommunityPost>(`/community-posts/${postId}/decide`, { approve, reason }),
    removePost: (postId: string, reason?: string) =>
      httpClient.post<CommunityPost>(`/community-posts/${postId}/remove`, { reason }),
    reactPost: (postId: string, type: ReactionKind) =>
      httpClient.put<ReactionSummary>(`/community-posts/${postId}/reaction`, { type }),
    unreactPost: (postId: string) =>
      httpClient.delete<ReactionSummary>(`/community-posts/${postId}/reaction`),
    postComments: (postId: string, query: Query) =>
      httpClient.get<Page<CommunityComment>>(`/community-posts/${postId}/comments`, { query }),
    commentPost: (postId: string, content: string, parentId?: string) =>
      httpClient.post<CommunityComment>(`/community-posts/${postId}/comments`, {
        content,
        parentId,
      }),
    deleteComment: (commentId: string) =>
      httpClient.delete<ActionResult>(`/community-comments/${commentId}`),
    removeComment: (commentId: string, reason?: string) =>
      httpClient.post<ActionResult>(`/community-comments/${commentId}/remove`, { reason }),
  },

  urgentRequests: {
    list: (query: Query) => httpClient.get<Page<UrgentRequest>>('/urgent-requests', { query }),
    detail: (id: string) => httpClient.get<UrgentRequest>(`/urgent-requests/${id}`),
    create: (body: {
      title: string;
      description: string;
      categoryId?: string;
      city?: string;
      maxBudget?: number;
    }) => httpClient.post<UrgentRequest>('/urgent-requests', body),
    close: (id: string, fulfilled: boolean) =>
      httpClient.post<UrgentRequest>(`/urgent-requests/${id}/close`, { fulfilled }),
  },

  cart: {
    get: () => httpClient.get<Cart>('/cart'),
    setItem: (productId: string, quantity: number) =>
      httpClient.put<Cart>('/cart/items', { productId, quantity }),
    removeItem: (productId: string) => httpClient.delete<Cart>(`/cart/items/${productId}`),
    clear: () => httpClient.delete<{ message: string }>('/cart'),
  },

  orders: {
    list: (query: Query) => httpClient.get<Page<Order>>('/orders', { query }),
    detail: (id: string) => httpClient.get<OrderDetail>(`/orders/${id}`),
    checkout: (body: Record<string, unknown>) => httpClient.post<Checkout>('/orders', body),
    /**
     * Un panier qui mêle plusieurs boutiques se scinde en une commande par
     * boutique, chacune avec son versement : elles se relisent ensemble ici,
     * pour être payées à la suite. Côté acheteur seulement.
     */
    checkoutGroup: (checkoutId: string) =>
      httpClient.get<Checkout>(`/orders/checkouts/${checkoutId}`),
    /**
     * Vente au comptoir : le client paie et repart avec le bien. La commande
     * naît terminée, avec son reçu, et le stock sort du disponible.
     */
    sellAtCounter: (body: CounterSale) => httpClient.post<OrderDetail>('/orders/counter', body),
    transition: (id: string, action: string, reason?: string, expectedDeliveryAt?: string) =>
      httpClient.post<OrderDetail>(`/orders/${id}/transition`, {
        action,
        reason,
        expectedDeliveryAt,
      }),
    /**
     * Refus à la réception : le motif est exigé, et les photos du bien sont
     * versées au litige que le refus ouvre.
     */
    refuseDelivery: (id: string, reason: string, evidenceUrls: Array<string>) =>
      httpClient.post<OrderDetail>(`/orders/${id}/transition`, {
        action: 'REFUSE_DELIVERY',
        reason,
        evidenceUrls,
      }),
  },

  payments: {
    declare: (
      orderId: string,
      body: {
        method: string;
        amount: number;
        reference?: string;
        proofUrl?: string;
        paidAt?: string;
        paidPlace?: string;
      },
    ) => httpClient.post<PaymentDeclaration>(`/orders/${orderId}/payment/declare`, body),
    /** La référence est-elle libre ? Interrogée pendant la saisie, avant l'envoi. */
    checkReference: (orderId: string, reference: string) =>
      httpClient.get<PaymentReferenceCheck>(`/orders/${orderId}/payment/reference-check`, {
        query: { reference },
      }),
    /** Opérateurs Mobile Money et codes USSD, servis par l'API et non figés ici. */
    mobileMoneyOperators: () =>
      httpClient.get<Array<MobileMoneyOperator>>('/mobile-money/operators'),
    confirm: (orderId: string) => httpClient.post<Receipt>(`/orders/${orderId}/payment/confirm`),
    reject: (orderId: string, reason: string) =>
      httpClient.post<PaymentDeclaration>(`/orders/${orderId}/payment/reject`, { reason }),
    receipt: (orderId: string) => httpClient.get<Receipt>(`/orders/${orderId}/payment/receipt`),
    verifyReceipt: (qrPayload: string) =>
      httpClient.post<ReceiptVerification>('/receipts/verify', { qrPayload }, { anonymous: true }),
  },

  disputes: {
    list: (query: Query) => httpClient.get<Page<Dispute>>('/disputes', { query }),
    detail: (id: string) => httpClient.get<Dispute>(`/disputes/${id}`),
    open: (body: {
      orderId: string;
      reason: string;
      description?: string;
      evidenceUrls?: Array<string>;
    }) => httpClient.post<Dispute>('/disputes', body),
    /**
     * Pièces versées au dossier par une partie, l'acheteur comme la boutique,
     * tant que la décision n'est pas rendue. Rend le dossier à jour.
     */
    addEvidence: (id: string, body: { note?: string; fileUrls?: Array<string> }) =>
      httpClient.post<Dispute>(`/disputes/${id}/evidence`, body),
    review: (id: string) => httpClient.post<Dispute>(`/disputes/${id}/review`),
    arbitrate: (id: string, body: { decision: DisputeDecision; decisionReason: string }) =>
      httpClient.post<Dispute>(`/disputes/${id}/arbitrate`, body),
  },

  sanctions: {
    list: (query: Query) => httpClient.get<Page<Sanction>>('/sanctions', { query }),
    create: (body: {
      type: SanctionType;
      reason: string;
      userId?: string;
      shopId?: string;
      disputeId?: string;
      endsAt?: string;
    }) => httpClient.post<Sanction>('/sanctions', body),
    lift: (id: string, note?: string) =>
      httpClient.post<Sanction>(`/sanctions/${id}/lift`, { note }),
  },

  inspections: {
    list: (query: Query) => httpClient.get<Page<Inspection>>('/inspections', { query }),
    detail: (id: string) => httpClient.get<Inspection>(`/inspections/${id}`),
    request: (body: {
      subjectType: 'SHOP' | 'PRODUCT';
      shopId?: string;
      productId?: string;
      proposedDate?: string;
      locationAddress?: string;
      contactName?: string;
      contactPhone?: string;
      message?: string;
    }) => httpClient.post<Inspection>('/inspections', body),
    schedule: (id: string, body: { scheduledAt: string; scheduledPlace: string }) =>
      httpClient.post<Inspection>(`/inspections/${id}/schedule`, body),
    report: (
      id: string,
      body: {
        approved: boolean;
        score: number;
        summary: string;
        reportFormUrl?: string;
        photoUrls?: Array<string>;
        gpsLat?: number;
        gpsLng?: number;
        /** Dossier complété pendant la visite : pièces vues ou collectées. */
        documents?: Record<string, unknown>;
      },
    ) => httpClient.post<Inspection>(`/inspections/${id}/report`, body),
    /** L'étape validateur (décision 3) : le badge se pose ici, jamais au rapport. */
    validate: (id: string, body: { approved: boolean; note?: string }) =>
      httpClient.post<Inspection>(`/inspections/${id}/validate`, body),
    cancel: (id: string, reason?: string) =>
      httpClient.post<Inspection>(`/inspections/${id}/cancel`, { reason }),
    assign: (id: string, certifierId: string) =>
      httpClient.post<Inspection>(`/inspections/${id}/assign`, { certifierId }),
  },

  certification: {
    /** L'entrée du vendeur : déclarer ses boutiques et demander la visite, en un geste. */
    request: (body: {
      shopIds?: Array<string>;
      shops?: Array<Record<string, unknown>>;
      proposedDate?: string;
      locationAddress?: string;
      contactName?: string;
      contactPhone?: string;
      message?: string;
    }) => httpClient.post<CertificationRequestResult>('/certification-requests', body),
  },

  subscriptions: {
    plans: () => httpClient.get<PlanCatalog>('/subscriptions/plans'),
    forShop: (shopId: string) => httpClient.get<ShopSubscription>(`/subscriptions/shops/${shopId}`),
    changePlan: (shopId: string, plan: string) =>
      httpClient.put<ShopSubscription>(`/subscriptions/shops/${shopId}`, { plan }),
  },

  billing: {
    invoices: (query: Query) => httpClient.get<Page<Invoice>>('/invoices', { query }),
    invoice: (id: string) => httpClient.get<Invoice>(`/invoices/${id}`),
    /** Ce qui est dû et pour quand, ce qui a été réglé, et la prochaine facture. */
    summary: (shopId: string) => httpClient.get<ShopBillingSummary>(`/billing/shops/${shopId}`),
    /** L'adresse du PDF : elle s'ouvre sans jeton, et expire en quelques minutes. */
    documentLink: (id: string) =>
      httpClient.post<InvoiceDocumentLink>(`/invoices/${id}/document-link`),
    /**
     * Le responsable déclare avoir réglé sa facture ; l'administration en prend
     * acte ensuite. La facture reste due jusqu'au pointage.
     */
    declareInvoicePayment: (id: string, paymentReference: string) =>
      httpClient.post<Invoice>(`/invoices/${id}/declare-payment`, { paymentReference }),
    /** Le suivi du recouvrement (B.4 étape 3), agrégé par le serveur. */
    overview: () => httpClient.get<BillingOverview>('/billing/overview'),
    /** Le pointage : l'administration constate le règlement, hors plateforme. */
    markPaid: (id: string, paymentReference?: string) =>
      httpClient.post<Invoice>(`/invoices/${id}/mark-paid`, { paymentReference }),
    cancelInvoice: (id: string) => httpClient.post<Invoice>(`/invoices/${id}/cancel`),
  },

  messaging: {
    conversations: (query: Query) =>
      httpClient.get<Page<Conversation>>('/conversations', { query }),
    /** Le fil ouvert : produit épinglé, proposition qui compte, côté de la table. */
    conversation: (id: string) => httpClient.get<ConversationDetail>(`/conversations/${id}`),
    messages: (id: string, query: Query) =>
      httpClient.get<Page<ChatMessage>>(`/conversations/${id}/messages`, { query }),
    send: (id: string, content: string) =>
      httpClient.post<ChatMessage>(`/conversations/${id}/messages`, { content }),
    /** Un vocal déjà déposé (`uploadFile`, dossier `voice-notes`), avec sa durée et sa forme d'onde. */
    sendVoice: (id: string, voice: VoiceNoteInput) =>
      httpClient.post<ChatMessage>(`/conversations/${id}/messages`, { voice }),
    /** Partage une publication dans la discussion, avec ou sans mot d'accompagnement. */
    shareListing: (id: string, listingId: string, content?: string) =>
      httpClient.post<ChatMessage>(`/conversations/${id}/messages`, {
        listingId,
        content: content?.trim() ? content.trim() : undefined,
      }),
    /**
     * Ouvrir ou rouvrir une discussion. Avec `productId`, le serveur en déduit
     * la boutique et son responsable : écrire depuis une fiche ne demande rien
     * d’autre que le produit.
     */
    start: (body: { participantIds?: Array<string>; shopId?: string; productId?: string }) =>
      httpClient.post<Conversation>('/conversations', { type: 'DIRECT', ...body }),
    markRead: (id: string) => httpClient.post<{ message: string }>(`/conversations/${id}/read`),

    // --- groupes ---------------------------------------------------------
    /**
     * Les personnes que l’on peut ajouter. Le serveur ne rend que celles que la
     * plateforme nous a déjà fait rencontrer : il n’existe pas d’annuaire des
     * membres, et un numéro de téléphone ne permet de retrouver personne.
     */
    contacts: (search?: string) =>
      httpClient.get<Array<Contact>>('/conversations/contacts', {
        query: search ? { search } : undefined,
      }),
    createGroup: (body: { title: string; participantIds: Array<string>; photoUrl?: string }) =>
      httpClient.post<Conversation>('/conversations', { type: 'GROUP', ...body }),
    updateGroup: (id: string, body: { title?: string; photoUrl?: string | null }) =>
      httpClient.patch<Conversation>(`/conversations/${id}`, body),
    addParticipants: (id: string, participantIds: Array<string>) =>
      httpClient.post<Conversation>(`/conversations/${id}/participants`, { participantIds }),
    removeParticipant: (id: string, userId: string) =>
      httpClient.delete<Conversation>(`/conversations/${id}/participants/${userId}`),
    setParticipantRole: (id: string, userId: string, role: 'ADMIN' | 'MEMBER') =>
      httpClient.patch<Conversation>(`/conversations/${id}/participants/${userId}/role`, { role }),
    leaveGroup: (id: string) => httpClient.post<{ message: string }>(`/conversations/${id}/leave`),
  },

  /**
   * Appels audio et vidéo. Seuls `start` et `join` rendent un jeton de pièce :
   * il ne circule nulle part ailleurs. La sonnerie et chaque changement d'état
   * arrivent par le canal temps réel (`call:incoming`, `call:changed`).
   */
  calls: {
    start: (conversationId: string, kind: CallKind) =>
      httpClient.post<Call>(`/conversations/${conversationId}/calls`, { kind }),
    detail: (id: string) => httpClient.get<Call>(`/calls/${id}`),
    /** Celui qui sonne chez moi, celui où je suis en ligne : relu au retour au premier plan. */
    active: () => httpClient.get<Array<Call>>('/calls/active'),
    join: (id: string) => httpClient.post<Call>(`/calls/${id}/join`),
    /** Refuser ce qui sonne, ou raccrocher : le serveur départage. Rejouable sans effet. */
    hangUp: (id: string) => httpClient.post<Call>(`/calls/${id}/hang-up`),
  },

  offers: {
    propose: (
      conversationId: string,
      body: { unitPrice: number; quantity?: number; productId?: string; message?: string },
    ) => httpClient.post<Offer>(`/conversations/${conversationId}/offers`, body),
    detail: (id: string) => httpClient.get<Offer>(`/offers/${id}`),
    accept: (id: string) => httpClient.post<Offer>(`/offers/${id}/accept`),
    decline: (id: string) => httpClient.post<Offer>(`/offers/${id}/decline`),
    withdraw: (id: string) => httpClient.post<Offer>(`/offers/${id}/withdraw`),
  },

  uploads: {
    // Le dépôt lui-même vit dans `upload.ts` : il est multipart, et la
    // construction de la pièce jointe dépend de la plateforme.
    remove: (id: string) => httpClient.delete<{ message: string }>(`/uploads/${id}`),
    /**
     * Autorisation de déposer une vidéo **sans passer par l'API** : elle part
     * droit chez l'hébergeur. La taille annoncée entre dans la signature, donc
     * elle doit être exacte.
     */
    videoTicket: (body: {
      bytes: number;
      durationMs: number;
      width: number;
      height: number;
      /** Destination : une publication du fil (défaut) ou de communauté. */
      folder?: 'listings' | 'communities';
    }) => httpClient.post<VideoTicket>('/uploads/video-ticket', body),
    /** Une fois l'envoi terminé : l'API vérifie ce qui est arrivé et rend l'adresse. */
    confirmVideo: (id: string) => httpClient.post<UploadedFile>(`/uploads/${id}/confirm`),
  },

  platform: {
    stats: () => httpClient.get<PlatformStats>('/stats/platform'),
    settings: () => httpClient.get<PlatformSettings>('/settings/platform'),
    updateSettings: (body: Partial<Record<keyof PlatformSettings, number>>) =>
      httpClient.patch<PlatformSettings>('/settings/platform', body),
    auditLogs: (query: Query) => httpClient.get<Page<AuditLog>>('/audit-logs', { query }),
  },

  notifications: {
    list: (query: Query) => httpClient.get<Page<Notification>>('/notifications', { query }),
    unreadCount: () => httpClient.get<{ count: number }>('/notifications/unread-count'),
    markRead: (id: string) => httpClient.post<Notification>(`/notifications/${id}/read`),
    markAllRead: () => httpClient.post<{ message: string }>('/notifications/read-all'),
    /**
     * Retire des notifications de la liste. Rien n'est supprimé sur le serveur :
     * elles cessent de s'afficher pour ce compte, et `restore` les fait revenir.
     */
    hide: (body: { ids?: Array<string>; all?: boolean }) =>
      httpClient.post<HiddenNotifications>('/notifications/hide', body),
    restore: (ids: Array<string>) =>
      httpClient.post<HiddenNotifications>('/notifications/restore', { ids }),
    /** Ce téléphone accepte d'être prévenu, application fermée. */
    enrollDevice: (body: PushDevice) =>
      httpClient.post<{ message: string }>('/notifications/devices', body),
    /**
     * Il ne l'accepte plus : déconnexion.
     *
     * `endsSession` : c'est le dernier geste d'une session, et elle peut déjà
     * être morte (compte suspendu). Sans ce drapeau, le 401 attendu relançait
     * la rotation du jeton, qui concluait « session perdue », qui rappelait la
     * déconnexion : le tour de trop décrit dans `http-client.ts`.
     */
    forgetDevice: (token: string) =>
      httpClient.post<{ message: string }>(
        '/notifications/devices/forget',
        { token },
        { endsSession: true },
      ),
  },
};
